import classNames from 'classnames'
import Link from 'next/link'
import { Fragment, memo, RefObject, useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import BSCard from 'react-bootstrap/Card'
import { forceCheck } from 'react-lazyload'

import Card from '@/components/Card/Card'
import Icon from '@/components/Icon/Icon'
import Spinner from '@/components/Spinner/Spinner'
import { useAds } from '@/contexts/ads'
import { update } from '@/store/cardlists/actions/update'
import { CardLabelVariantType, CardListType, PageCardType } from '@/types/card'
import { BORDER_WIDTH, CARD_WIDTH, GRID_GAP } from '@/utils/constants'
import { bracketStringToNumber, capitalizeBracket } from '@/utils/helpers'
import useDirectories from '@/utils/useDirectories'

import ContentHint from './ContentHint'
import styles from './Grid.module.scss'

const NUM_CARDS_BEFORE_CONTENT_HINT_DESKTOP = 23
const NUM_CARDS_BEFORE_CONTENT_HINT_MOBILE = 10

type Props = {
  cardLists: CardListType[]
  enableCount?: boolean
  groupCardLists?: boolean
  hideSidebar?: boolean
  labelVariant?: CardLabelVariantType
  loading: boolean
  numCols: number
  pageCommander?: PageCardType
  sectionRefs: RefObject<HTMLDivElement>[]
}

const Grid = ({
  cardLists,
  enableCount,
  groupCardLists,
  labelVariant,
  loading,
  numCols,
  pageCommander,
  sectionRefs,
}: Props) => {
  const { loadAds } = useAds()
  const { query, subsubpage } = useDirectories()
  const [isRendered, setIsRendered] = useState(false)

  const isDesktop = numCols > 1
  groupCardLists = groupCardLists && isDesktop

  const cardListStyles = (showHeader?: boolean) =>
    groupCardLists ? classNames(styles.cardlist, styles.grouped, { 'pt-2': !showHeader }) : styles.cardlist
  const headerStyles = classNames(styles.header, { 'mx-2': groupCardLists })

  const numCardsBeforeContentHintDesktop = query?.incontent ? 42 : NUM_CARDS_BEFORE_CONTENT_HINT_DESKTOP
  const numCardsBeforeContentHint = isDesktop ? numCardsBeforeContentHintDesktop : NUM_CARDS_BEFORE_CONTENT_HINT_MOBILE

  // Start at half the number of cards before content hint
  // so that the first content hint starts sooner
  let cardNum = Math.floor(numCardsBeforeContentHint / 2)

  // Cards are lazy loaded, so we need to force a check on mount and when the cardLists change
  useEffect(() => {
    if (isRendered) {
      setIsRendered(false)
    }
  }, [cardLists, numCols])

  useEffect(() => {
    if (!isRendered) {
      setIsRendered(true)

      // forceCheck runs too early, so we need to wait 1 ms
      setTimeout(() => {
        forceCheck()
      }, 1)
    }
  }, [isRendered])

  return (
    <>
      <div
        className={styles.grid}
        style={
          groupCardLists
            ? {
                gap: `${GRID_GAP}px`,
                gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH + 2 * GRID_GAP + 2 * BORDER_WIDTH}px, 1fr))`,
                paddingTop: 2,
              }
            : undefined
        }
      >
        {cardLists.map(({ cardviews, header, href, more, tag }, i) => {
          if (cardviews.length === 0) return null

          // Show header > 1 cardlist, skip first unless no sidebar
          const showHeader =
            cardLists.length > 1 &&
            !(!groupCardLists && i === 0 && cardviews.length + cardLists[1].cardviews.length > numCols)

          // Increase cardList length to account for ads
          const numAds =
            Math.floor((cardNum + cardviews.length) / numCardsBeforeContentHint) -
            Math.floor(cardNum / numCardsBeforeContentHint)
          const cardListLength = cardviews.length + (loadAds === true ? 2 * numAds : 0)

          return (
            <Fragment key={`${tag}-container`}>
              <div
                className={cardListStyles(showHeader)}
                id={tag}
                key={tag}
                ref={sectionRefs[i]}
                style={{
                  gridColumn: `span ${groupCardLists ? Math.min(cardListLength, numCols) : numCols}`,
                }}
              >
                <div className={classNames({ 'shadow-sm': groupCardLists })}>
                  {/* Header */}
                  {showHeader && (
                    <BSCard.Title key={`${tag}-header`}>
                      {href ? (
                        <Link className={headerStyles} href={href}>
                          {header}
                          {enableCount && <span> ({cardviews.length})</span>}
                        </Link>
                      ) : header ? (
                        <div className={headerStyles}>
                          {header}
                          {enableCount && <span> ({cardviews.length})</span>}
                        </div>
                      ) : null}
                    </BSCard.Title>
                  )}
                  {/* Warning for Game Changers in Brackets 1-2 */}
                  {showHeader && header === 'Game Changers' && ['exhibition', 'core'].includes(subsubpage) && (
                    <Alert className='mb-2' variant='danger'>
                      <Icon className='me-2' icon='circleExclamation' />
                      <b>
                        {capitalizeBracket(subsubpage)} (Bracket {bracketStringToNumber(subsubpage)})
                      </b>{' '}
                      typically <b>disallows Game Changers</b>. Check with your playgroup before including any.
                    </Alert>
                  )}
                  <div
                    className={styles.grid}
                    style={
                      groupCardLists
                        ? {
                            gap: `${3 * GRID_GAP + 2 * BORDER_WIDTH}px`,
                            gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr))`,
                            padding: `${GRID_GAP}px`,
                            paddingTop: 0,
                          }
                        : undefined
                    }
                  >
                    {cardviews.map((cardview) => {
                      cardNum += 1
                      const showAd = loadAds === true && cardNum % numCardsBeforeContentHint === 0
                      return (
                        <Fragment key={`container-${cardNum}`}>
                          {/* Content Hint */}
                          {showAd && (
                            <div
                              className={classNames(styles.grid, { [styles.ad]: numCols > 1, 'w-100': numCols === 1 })}
                              key={`ad-${cardNum}`}
                              style={
                                groupCardLists
                                  ? {
                                      gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr))`,
                                      padding: `${GRID_GAP}px`,
                                    }
                                  : undefined
                              }
                            >
                              <ContentHint key={`ad-${cardNum}`} numCols={numCols} wide={isDesktop} />
                            </div>
                          )}
                          {/* Card */}
                          <div
                            className={classNames('d-flex justify-content-center', { 'mb-2': !groupCardLists })}
                            key={`cardContainer-${cardNum}`}
                          >
                            <Card
                              card={cardview}
                              key={`card-${cardNum}`}
                              labelVariant={labelVariant}
                              pageCommander={pageCommander}
                            />
                          </div>
                        </Fragment>
                      )
                    })}
                  </div>
                  {/* Load More Button */}
                  {more && (
                    <span className={styles.loadMore} key={`load-more-${tag}`}>
                      <Button
                        disabled={loading}
                        onClick={() => {
                          update({ moreIndex: 0 })
                        }}
                      >
                        {loading ? <Spinner /> : 'Load More'}
                      </Button>
                    </span>
                  )}
                </div>
              </div>
            </Fragment>
          )
        })}
      </div>
    </>
  )
}

export default memo(Grid)
